import {
  Box, Card, CardHeader, Skeleton,
} from '@mui/material';
import {
  FC, useMemo, useRef,
} from 'react';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/system';
import { useTheme } from '@mui/material/styles';
import Chart, { useChart, ApexOptions } from '@mgm/common/components/charts';
import { CustomNoRowsOverlay } from '@mgm/common/components';
import { AnimatedDivider } from '../../../../components';
import { useAppSelector } from '../../../../hooks/useAppStore';
import { getIsOrdersLoading } from '../../../../store/orders/selectors';

const CHART_HEIGHT = 400;
const LEGEND_HEIGHT = 72;

const StyledChart = styled('div')(({ theme }) => ({
  height: CHART_HEIGHT,
  marginTop: theme.spacing(5),
  '& .apexcharts-canvas svg': {
    height: CHART_HEIGHT,
  },
  '& .apexcharts-canvas svg,.apexcharts-canvas foreignObject': {
    overflow: 'visible',
  },
  '& .apexcharts-legend': {
    height: LEGEND_HEIGHT,
    alignContent: 'center',
    borderTop: `solid 1px ${theme.palette.divider}`,
    top: `calc(${CHART_HEIGHT - LEGEND_HEIGHT}px) !important`,
  },
}));

type OrdersByTeamDonutChartProps = {
  teams: Array<{
    id: number;
    name: string;
    totalOrders: number;
  }>;
};

const TeamOrdersDonutChart: FC<OrdersByTeamDonutChartProps> = ({ teams }) => {
  const { t } = useTranslation(['orders', 'common']);
  const theme = useTheme();
  const chartRef = useRef<any>(null);
  const isOrdersLoading = useAppSelector(getIsOrdersLoading);

  const chartData = useMemo(() => {
    const categories = teams.map((team) => team.name || 'Unknown');
    const seriesData = teams.map((team) => team.totalOrders || 0);
    return { categories, seriesData };
  }, [teams]);

  const chartOptions: ApexOptions = useChart({
    chart: {
      type: 'donut',
      events: {
        mounted: (chart) => {
          chartRef.current = chart;
        },
      },
    },
    labels: chartData.categories,
    colors: [
      '#9E86FF', '#83CFFF', '#60F1C8', '#FFEF5A', '#FF8F6D',
      '#826AF9', '#2D99FF', '#2CD9C5', '#FFE700', '#FF6C40',
    ],
    stroke: { colors: [theme.palette.background.paper] },
    legend: { floating: true, horizontalAlign: 'center' },
    tooltip: {
      y: {
        formatter: (value: number) => `${value} ${t('orders', { ns: 'orders' })}`,
      },
    },
    plotOptions: {
      pie: {
        donut: {
          size: '85%',
          labels: {
            value: {
              formatter: (val: string) => val,
            },
            total: {
              show: true,
              label: 'Total',
              formatter: () => chartData.seriesData.reduce((sum, val) => sum + val, 0).toString(),
            },
          },
        },
      },
    },
  });

  return (
    <Card>
      <CardHeader
        sx={{ p: 2 }}
        title="Orders By Team"
      />
      <AnimatedDivider loading={isOrdersLoading} />
      <Box sx={{ mt: 3, mx: 3 }} dir="ltr">
        {isOrdersLoading ? (
          <Box pt={3}>
            {[...Array(5)].map((_, i) => (
              <Skeleton key={i?.toString()} variant="rectangular" height={40} sx={{ mb: 2 }} />
            ))}
          </Box>
        ) : (
          <StyledChart dir="ltr">
            {chartData.categories.length && chartData.seriesData.length ? (
              <Chart
                type="donut"
                series={chartData.seriesData}
                options={chartOptions}
                height={250}
              />
            ) : (
              <CustomNoRowsOverlay text={t('noDataForToday', { ns: 'common' })} />
            )}
          </StyledChart>
        )}
      </Box>
    </Card>
  );
};

export { TeamOrdersDonutChart };
